import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Alert from 'components/Alert';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import productService from 'v2/services/product';

import type { SubscriptionPlan } from 'v2/interfaces/product';

import type IProduct from 'interfaces/IProduct';

type ActionsMenuProps = {
  anchorEl: null | HTMLElement;
  product: IProduct;
  plan: SubscriptionPlan;
  closeMenu: () => void;
  openChangeModal: () => void;
  openLinksModal: () => void;
};

function ActionsMenu({ anchorEl, product, plan, openLinksModal, closeMenu, openChangeModal }: ActionsMenuProps) {
  const queryClient = useQueryClient();
  const history = useHistory();
  const { plan_id: planId } = plan;
  const isExcludingPlan = React.useRef(false);

  const deletePlanMutation = useMutation((planId: number) => productService.deletePlan(planId));
  const clonePlanMutation = useMutation((planId: number) => productService.clonePlan(planId));

  async function handleDeleteClick() {
    if (isExcludingPlan.current) return;
    isExcludingPlan.current = true;

    const confirm = await Alert.confirm({
      title: 'Confirmação de exclusão',
      message:
        'Caso clique em excluir, não poderá mais fazer vendas deste plano e seus contratos com clientes continuarão cobrando normalmente. Deseja continuar?',
      confirmation: true,
      buttonOk: 'Excluir',
      buttonCancel: 'Cancelar',
    });

    isExcludingPlan.current = false;

    closeMenu();

    if (!confirm) return;

    const toastId = toast.info('Excluindo plano...', { autoClose: 10000 });

    try {
      await deletePlanMutation.mutateAsync(planId);
      await queryClient.invalidateQueries(['product', product.id, 'plans']);
      toast.dismiss(toastId);
      toast.success('Plano excluído com sucesso!');
    } catch (err) {
      toast.dismiss(toastId);
      toast.error('Falha ao deletar plano');
      console.error(err);
    }
  }

  async function handleCloneClick() {
    const toastId = toast.info('Duplicando plano...', { autoClose: 10000 });
    try {
      closeMenu();
      await clonePlanMutation.mutateAsync(planId);
      await queryClient.invalidateQueries(['product', product.id, 'plans']);
      toast.dismiss(toastId);
      toast.success('Plano duplicado com sucesso!');
    } catch (err) {
      toast.dismiss(toastId);
      toast.error('Falha ao duplicar plano');
      console.error(err);
    }
  }

  function handleEditClick() {
    history.push(`/producer/product/${product.id}/subscription-plan/${plan.plan_id}/edit`, {
      product,
    });
  }

  function handleChangePlanClick() {
    openChangeModal();
    closeMenu();
  }

  return (
    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
      <MenuItem id='edit-plan-button' onClick={handleEditClick}>
        Editar
      </MenuItem>
      <MenuItem id='clone-plan-button' onClick={handleCloneClick} disabled={clonePlanMutation.isLoading}>
        Duplicar
      </MenuItem>
      <MenuItem id='links-plan-button' onClick={openLinksModal}>
        Links deste plano
      </MenuItem>
      <MenuItem id='change-plan-button' onClick={handleChangePlanClick}>
        Configurar troca de plano
      </MenuItem>
      <MenuItem id='create-offer-button' onClick={() => ({})}>
        Criar oferta promocional
      </MenuItem>
      {!plan.plan_is_default && (
        <MenuItem id='delete-plan-button' onClick={handleDeleteClick} disabled={deletePlanMutation.isLoading || isExcludingPlan.current}>
          Excluir
        </MenuItem>
      )}
    </Menu>
  );
}

export { ActionsMenu };
